// aluElevation.js — parametric aluminum elevation generator (catalog visual, pre-confirmation)
// Pure functions: (params) -> SVG string. No DOM, no deps. Works in Node (PDF) + React (modal).
// Templates: sashWindow (Fixed/Casement/Awning/Tilt&Turn) · singleDoor (Hinged/Storefront)
// Grilles: Colonial / Prairie / Perimeter / Diamond / Top Row. aluElevation(opts) = dispatcher.

const PALETTE = {
  frameFace: '#1c1d21',
  frameEdge: '#9aa0a8',
  sashEdge:  '#3a3b40',
  glassTop:  '#eaf1f6',
  glassBot:  '#cfdae3',
  sheen:     '#ffffff',
  glyph:     '#475569',
  dim:       '#94a3b8',
  text:      '#334155',
};

// Frame stroke width. Prefers thickness_class (alu_models, migr 056:
// Narrow / Standard / Heavy / Extra-Heavy) over free text, so a 308S no
// longer draws like a 152S and a line with no catalog photo picked no
// longer falls back to Standard regardless of series.
// Falls back to keyword matching on the old `line` string when the model
// carries no thickness_class (23 of 30 models are still NULL).
function frameWeight(line = '', thickness_class = '') {
  const tc = String(thickness_class).toLowerCase().replace(/[\s_]/g, '-');
  if (tc.includes('extra')) return 17;
  if (tc.includes('heavy')) return 13;
  if (tc.includes('narrow')) return 6;
  if (tc.includes('standard')) return 9;
  const l = String(line).toLowerCase();
  if (l.includes('narrow')) return 6;
  if (l.includes('heavy'))  return 13;
  return 9;
}

const GLASS_DEFS =
  `<defs><linearGradient id="gGlass" x1="0" y1="0" x2="0" y2="1">`
  + `<stop offset="0" stop-color="${PALETTE.glassTop}"/>`
  + `<stop offset="1" stop-color="${PALETTE.glassBot}"/></linearGradient></defs>`;

function glassPanel(x, y, w, h) {
  w = Math.max(0, w); h = Math.max(0, h);
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="url(#gGlass)" stroke="${PALETTE.sashEdge}" stroke-width="1.25"/>`
    + `<polygon points="${x + w * 0.12},${y} ${x + w * 0.42},${y} ${x + w * 0.12},${y + h} ${x},${y + h}" fill="${PALETTE.sheen}" opacity="0.18"/>`;
}

function opGlyph(op, gx, gy, gw, gh) {
  const cx = gx + gw / 2, cy = gy + gh / 2;
  const tri = (ax, ay, b1x, b1y, b2x, b2y) =>
    `<polyline points="${b1x},${b1y} ${ax},${ay} ${b2x},${b2y}" fill="none" `
    + `stroke="${PALETTE.glyph}" stroke-width="1.6" stroke-dasharray="6 4" `
    + `stroke-linejoin="round" stroke-linecap="round"/>`;
  switch (op) {
    case 'casement-l':
    case 'hinge-l':   return tri(gx, cy,       gx + gw, gy, gx + gw, gy + gh);
    case 'casement-r':
    case 'hinge-r':   return tri(gx + gw, cy,  gx, gy,      gx, gy + gh);
    case 'awning':    return tri(cx, gy,       gx, gy + gh, gx + gw, gy + gh);
    case 'hopper':    return tri(cx, gy + gh,  gx, gy,      gx + gw, gy);
    case 'tilt-turn': return tri(cx, gy, gx, gy + gh, gx + gw, gy + gh)
                           + tri(gx, cy, gx + gw, gy, gx + gw, gy + gh);
    case 'fixed':
    default:          return '';
  }
}

// ── grilles (SDL/GBG muntins over the glass) ────────────────────────────────
function parseGrilles(opts) {
  const on = opts.grilles_sdl === true || String(opts.grilles_sdl).toLowerCase() === 'true';
  if (!on) return null;
  const cols = parseInt(opts.grille_cols, 10) || null;
  const rows = parseInt(opts.grille_rows, 10) || null;
  const ptype = String(opts.grille_pattern || '').toLowerCase();
  if (ptype) {
    if (ptype.includes('colonial') || ptype.includes('grid')) return { kind: 'colonial', cols: cols || 3, rows: rows || 3 };
    if (ptype.includes('prairie'))   return { kind: 'prairie' };
    if (ptype.includes('perimeter') || ptype.includes('marginal')) return { kind: 'perimeter' };
    if (ptype.includes('diamond'))   return { kind: 'diamond' };
    if (ptype.includes('top'))       return { kind: 'toprow', cols: cols || 3 };
    if (ptype.includes('custom'))    return (cols && rows) ? { kind: 'colonial', cols, rows } : null;
  }
  const txt = String(opts.grilles_pattern || '');
  const m = txt.match(/(\d+)\s*[x×X]\s*(\d+)/);
  if (m) return { kind: 'colonial', cols: +m[1], rows: +m[2] };
  if (/prairie/i.test(txt))            return { kind: 'prairie' };
  if (/perimeter|marginal/i.test(txt)) return { kind: 'perimeter' };
  if (/diamond/i.test(txt))            return { kind: 'diamond' };
  if (/colonial|grid/i.test(txt))      return { kind: 'colonial', cols: 3, rows: 3 };
  return null;
}

function clipSeg(x1, y1, x2, y2, rx, ry, rw, rh) {
  const xmin = rx, xmax = rx + rw, ymin = ry, ymax = ry + rh;
  let t0 = 0, t1 = 1; const dx = x2 - x1, dy = y2 - y1;
  const p = [-dx, dx, -dy, dy], q = [x1 - xmin, xmax - x1, y1 - ymin, ymax - y1];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) { if (q[i] < 0) return null; }
    else { const r = q[i] / p[i]; if (p[i] < 0) { if (r > t1) return null; if (r > t0) t0 = r; } else { if (r < t0) return null; if (r < t1) t1 = r; } }
  }
  return [x1 + t0 * dx, y1 + t0 * dy, x1 + t1 * dx, y1 + t1 * dy];
}

function grilleXML(g, x, y, w, h) {
  if (!g) return '';
  const M = PALETTE.frameFace, sw = 1.4;
  const line = (a, b, c, d, wt = sw) => `<line x1="${a}" y1="${b}" x2="${c}" y2="${d}" stroke="${M}" stroke-width="${wt}"/>`;
  let s = '';
  if (g.kind === 'colonial') {
    const cols = Math.max(1, g.cols || 1), rows = Math.max(1, g.rows || 1);
    for (let i = 1; i < cols; i++) { const X = x + w * i / cols; s += line(X, y, X, y + h); }
    for (let j = 1; j < rows; j++) { const Y = y + h * j / rows; s += line(x, Y, x + w, Y); }
  } else if (g.kind === 'prairie') {
    const f = 0.18;
    s += line(x + w * f, y, x + w * f, y + h) + line(x + w * (1 - f), y, x + w * (1 - f), y + h);
    s += line(x, y + h * f, x + w, y + h * f) + line(x, y + h * (1 - f), x + w, y + h * (1 - f));
  } else if (g.kind === 'perimeter') {
    const f = 0.15;
    s += `<rect x="${x + w * f}" y="${y + h * f}" width="${w * (1 - 2 * f)}" height="${h * (1 - 2 * f)}" fill="none" stroke="${M}" stroke-width="${sw}"/>`;
  } else if (g.kind === 'diamond') {
    const step = Math.max(18, w / 4);
    for (let k = -Math.ceil(h / step); k <= Math.ceil((w + h) / step); k++) {
      const ox = x + k * step;
      const a = clipSeg(ox, y, ox + h, y + h, x, y, w, h);
      const b = clipSeg(ox, y + h, ox + h, y, x, y, w, h);
      if (a) s += line(a[0], a[1], a[2], a[3], 1.2);
      if (b) s += line(b[0], b[1], b[2], b[3], 1.2);
    }
  } else if (g.kind === 'toprow') {
    const cols = Math.max(1, g.cols || 3), ty = y + h * 0.28;
    s += line(x, ty, x + w, ty);
    for (let i = 1; i < cols; i++) { const X = x + w * i / cols; s += line(X, y, X, ty); }
  }
  return s;
}

function dimsXML(sx, sy, w, h, width_in, height_in) {
  return `
    <line x1="${sx}" y1="${sy + h + 12}" x2="${sx + w}" y2="${sy + h + 12}" stroke="${PALETTE.dim}" stroke-width="1"/>
    <text x="${sx + w / 2}" y="${sy + h + 24}" text-anchor="middle" font-size="11" fill="${PALETTE.text}" font-family="Arial">${width_in}"</text>
    <line x1="${sx + w + 12}" y1="${sy}" x2="${sx + w + 12}" y2="${sy + h}" stroke="${PALETTE.dim}" stroke-width="1"/>
    <text x="${sx + w + 22}" y="${sy + h / 2}" text-anchor="middle" font-size="11" fill="${PALETTE.text}" font-family="Arial" transform="rotate(90 ${sx + w + 22} ${sy + h / 2})">${height_in}"</text>`;
}

function aspect(width_in, height_in, maxPx) {
  const ar = width_in / height_in;
  return ar >= 1 ? { w: maxPx, h: maxPx / ar } : { w: maxPx * ar, h: maxPx };
}

function sashWindow(opts = {}) {
  const { width_in = 36, height_in = 60, op = 'fixed', line = 'Thermal Break', thickness_class = '',
          grilles = null, showDims = false, maxPx = 230, pad = 10 } = opts;
  const { w, h } = aspect(width_in, height_in, maxPx);
  const fw = frameWeight(line, thickness_class);
  const sx = pad, sy = pad, ix = sx + fw, iy = sy + fw, iw = w - 2 * fw, ih = h - 2 * fw;
  const dimPad = showDims ? 26 : 0;
  return `<svg viewBox="0 0 ${(w + 2 * pad + dimPad).toFixed(0)} ${(h + 2 * pad + dimPad).toFixed(0)}" xmlns="http://www.w3.org/2000/svg">
  ${GLASS_DEFS}
  <rect x="${sx}" y="${sy}" width="${w}" height="${h}" rx="2" fill="${PALETTE.frameFace}" stroke="${PALETTE.frameEdge}" stroke-width="0.75"/>
  ${glassPanel(ix, iy, iw, ih)}
  ${grilleXML(grilles, ix, iy, iw, ih)}
  ${opGlyph(op, ix, iy, iw, ih)}
  ${showDims ? dimsXML(sx, sy, w, h, width_in, height_in) : ''}
</svg>`;
}

function singleDoor(opts = {}) {
  const { width_in = 36, height_in = 96, op = 'hinge-l', line = 'Thermal Break', thickness_class = '',
          style = 'hinged', transom = false, grilles = null,
          showDims = false, maxPx = 240, pad = 10 } = opts;
  const { w, h } = aspect(width_in, height_in, maxPx);
  const fw = frameWeight(line, thickness_class);
  const sx = pad, sy = pad;
  const ox = sx + fw, oy = sy + fw, ow = w - 2 * fw, oh = h - 2 * fw;
  const pxIn = h / height_in;
  const lr = Math.max(4, fw * 0.85);
  const dimPad = showDims ? 26 : 0;
  let parts = '';
  let leafTop = oy; const leafBottom = oy + oh;
  if (transom) {
    const tH = Math.min(oh * 0.22, Math.max(16, 14 * pxIn));
    parts += glassPanel(ox + lr, oy + lr, ow - 2 * lr, tH - lr);
    parts += `<rect x="${ox}" y="${oy + tH}" width="${ow}" height="${fw}" fill="${PALETTE.frameFace}"/>`;
    leafTop = oy + tH + fw;
  }
  parts += `<rect x="${ox}" y="${leafTop}" width="${ow}" height="${leafBottom - leafTop}" fill="none" stroke="${PALETTE.sashEdge}" stroke-width="1"/>`;
  const railIn = style === 'storefront' ? 10 : 6;
  const brH = Math.min((leafBottom - leafTop) * 0.32, Math.max(12, railIn * pxIn));
  const gx = ox + lr, gy = leafTop + lr;
  const gw = ow - 2 * lr, gh = (leafBottom - leafTop) - lr - brH;
  if (style === 'storefront') {
    const midH = fw * 0.8;
    const upperH = gh * 0.55;
    parts += glassPanel(gx, gy, gw, upperH);
    parts += `<rect x="${gx}" y="${gy + upperH}" width="${gw}" height="${midH}" fill="${PALETTE.frameFace}"/>`;
    parts += glassPanel(gx, gy + upperH + midH, gw, gh - upperH - midH);
    parts += opGlyph(op, gx, gy, gw, gh);
  } else {
    parts += glassPanel(gx, gy, gw, gh);
    parts += grilleXML(grilles, gx, gy, gw, gh);
    parts += opGlyph(op, gx, gy, gw, gh);
  }
  return `<svg viewBox="0 0 ${(w + 2 * pad + dimPad).toFixed(0)} ${(h + 2 * pad + dimPad).toFixed(0)}" xmlns="http://www.w3.org/2000/svg">
  ${GLASS_DEFS}
  <rect x="${sx}" y="${sy}" width="${w}" height="${h}" rx="2" fill="${PALETTE.frameFace}" stroke="${PALETTE.frameEdge}" stroke-width="0.75"/>
  ${parts}
  ${showDims ? dimsXML(sx, sy, w, h, width_in, height_in) : ''}
</svg>`;
}

// ── doubleDoor: French (both leaves operable) ───────────────────────────────
function doubleDoor(opts = {}) {
  const { width_in = 60, height_in = 96, line = 'Thermal Break', thickness_class = '',
          transom = false, grilles = null, showDims = false, maxPx = 240, pad = 10 } = opts;
  const { w, h } = aspect(width_in, height_in, maxPx);
  const fw = frameWeight(line, thickness_class);
  const sx = pad, sy = pad;
  const ox = sx + fw, oy = sy + fw, ow = w - 2 * fw, oh = h - 2 * fw;
  const pxIn = h / height_in;
  const lr = Math.max(4, fw * 0.85);
  const dimPad = showDims ? 26 : 0;
  let parts = '';
  let leafTop = oy; const leafBottom = oy + oh;
  if (transom) {
    const tH = Math.min(oh * 0.22, Math.max(16, 14 * pxIn));
    parts += glassPanel(ox + lr, oy + lr, ow - 2 * lr, tH - lr);
    parts += `<rect x="${ox}" y="${oy + tH}" width="${ow}" height="${fw}" fill="${PALETTE.frameFace}"/>`;
    leafTop = oy + tH + fw;
  }
  const stile = Math.max(4, fw);
  const leafW = (ow - stile) / 2;
  const brH = Math.min((leafBottom - leafTop) * 0.30, Math.max(12, 6 * pxIn));
  const leaf = (lx, op) => {
    let s = `<rect x="${lx}" y="${leafTop}" width="${leafW}" height="${leafBottom - leafTop}" fill="none" stroke="${PALETTE.sashEdge}" stroke-width="1"/>`;
    const gx = lx + lr, gy = leafTop + lr;
    const gw = leafW - 2 * lr, gh = (leafBottom - leafTop) - lr - brH;
    s += glassPanel(gx, gy, gw, gh);
    s += grilleXML(grilles, gx, gy, gw, gh);
    s += opGlyph(op, gx, gy, gw, gh);
    return s;
  };
  parts += leaf(ox, 'hinge-l');
  parts += `<rect x="${ox + leafW}" y="${leafTop}" width="${stile}" height="${leafBottom - leafTop}" fill="${PALETTE.frameFace}"/>`;
  parts += leaf(ox + leafW + stile, 'hinge-r');
  return `<svg viewBox="0 0 ${(w + 2 * pad + dimPad).toFixed(0)} ${(h + 2 * pad + dimPad).toFixed(0)}" xmlns="http://www.w3.org/2000/svg">
  ${GLASS_DEFS}
  <rect x="${sx}" y="${sy}" width="${w}" height="${h}" rx="2" fill="${PALETTE.frameFace}" stroke="${PALETTE.frameEdge}" stroke-width="0.75"/>
  ${parts}
  ${showDims ? dimsXML(sx, sy, w, h, width_in, height_in) : ''}
</svg>`;
}

function slidingTrack(opts = {}) {
  const { width_in = 72, height_in = 60, line = 'Thermal Break', thickness_class = '', grilles = null,
          panel_config = 'OX', showDims = false, maxPx = 240, pad = 10 } = opts;
  const { w, h } = aspect(width_in, height_in, maxPx);
  const fw = frameWeight(line, thickness_class);
  const sx = pad, sy = pad;
  const ox = sx + fw, oy = sy + fw, ow = w - 2 * fw, oh = h - 2 * fw;
  const dimPad = showDims ? 26 : 0;
  let cfg = String(panel_config || '').toUpperCase().replace(/[^OX]/g, '');
  if (!cfg) cfg = 'OX';
  const n = cfg.length;
  const mull = Math.max(2, fw * 0.5);
  const panelW = (ow - mull * (n - 1)) / n;
  const arrow = (cx, cy, dir, len) => {
    const x1 = cx - dir * len / 2, x2 = cx + dir * len / 2;
    const head = Math.max(4, len * 0.16);
    return `<line x1="${x1}" y1="${cy}" x2="${x2}" y2="${cy}" stroke="${PALETTE.glyph}" stroke-width="2" stroke-linecap="round"/>`
      + `<polyline points="${x2 - dir * head},${cy - head} ${x2},${cy} ${x2 - dir * head},${cy + head}" fill="none" stroke="${PALETTE.glyph}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
  };
  let parts = '';
  for (let i = 0; i < n; i++) {
    const px = ox + i * (panelW + mull);
    if (i > 0) parts += `<rect x="${px - mull}" y="${oy}" width="${mull}" height="${oh}" fill="${PALETTE.frameFace}"/>`;
    const gx = px + 2, gy = oy + 2, gw = panelW - 4, gh = oh - 4;
    parts += glassPanel(gx, gy, gw, gh);
    parts += grilleXML(grilles, gx, gy, gw, gh);
    if (cfg[i] === 'X') {
      let dir = 0, best = Infinity;
      for (let j = 0; j < n; j++) if (cfg[j] === 'O') { const d = Math.abs(j - i); if (d < best) { best = d; dir = Math.sign(j - i); } }
      if (dir === 0) dir = (i < n / 2 ? 1 : -1);
      parts += arrow(px + panelW / 2, oy + oh / 2, dir, panelW * 0.5);
    }
  }
  return `<svg viewBox="0 0 ${(w + 2 * pad + dimPad).toFixed(0)} ${(h + 2 * pad + dimPad).toFixed(0)}" xmlns="http://www.w3.org/2000/svg">
  ${GLASS_DEFS}
  <rect x="${sx}" y="${sy}" width="${w}" height="${h}" rx="2" fill="${PALETTE.frameFace}" stroke="${PALETTE.frameEdge}" stroke-width="0.75"/>
  ${parts}
  ${showDims ? dimsXML(sx, sy, w, h, width_in, height_in) : ''}
</svg>`;
}
function bifold(opts = {}) {
  const { width_in = 96, height_in = 84, line = 'Thermal Break', thickness_class = '', grilles = null,
          panel_config = '', opens = '', showDims = false, maxPx = 240, pad = 10 } = opts;
  const { w, h } = aspect(width_in, height_in, maxPx);
  const fw = frameWeight(line, thickness_class);
  const sx = pad, sy = pad;
  const ox = sx + fw, oy = sy + fw, ow = w - 2 * fw, oh = h - 2 * fw;
  const dimPad = showDims ? 26 : 0;
  const cfg = String(panel_config || '').toUpperCase().replace(/[^OX]/g, '');
  let n = cfg.length || 4;
  if (n < 2) n = 4;
  const biPart = /center|bi-?part|bipart/i.test(String(opens) + ' ' + String(panel_config));
  const mull = Math.max(3, fw * 0.6);
  const panelW = (ow - mull * (n - 1)) / n;
  const chev = (cx, cy, dir, s) =>
    `<polyline points="${cx - dir * s},${cy - s} ${cx},${cy} ${cx - dir * s},${cy + s}" fill="none" stroke="${PALETTE.glyph}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
  let parts = '';
  for (let i = 0; i < n; i++) {
    const px = ox + i * (panelW + mull);
    if (i > 0) parts += `<rect x="${px - mull}" y="${oy}" width="${mull}" height="${oh}" fill="${PALETTE.frameFace}"/>`;
    const gx = px + 2, gy = oy + 2, gw = panelW - 4, gh = oh - 4;
    parts += glassPanel(gx, gy, gw, gh);
    parts += grilleXML(grilles, gx, gy, gw, gh);
    let dir = -1;
    if (biPart) dir = (i < n / 2 ? 1 : -1);
    const cs = Math.min(panelW * 0.16, oh * 0.05, 9);
    parts += chev(px + panelW / 2, oy + oh / 2, dir, cs);
  }
  return `<svg viewBox="0 0 ${(w + 2 * pad + dimPad).toFixed(0)} ${(h + 2 * pad + dimPad).toFixed(0)}" xmlns="http://www.w3.org/2000/svg">
  ${GLASS_DEFS}
  <rect x="${sx}" y="${sy}" width="${w}" height="${h}" rx="2" fill="${PALETTE.frameFace}" stroke="${PALETTE.frameEdge}" stroke-width="0.75"/>
  ${parts}
  ${showDims ? dimsXML(sx, sy, w, h, width_in, height_in) : ''}
</svg>`;
}
function windowWall(opts = {}) {
  const { width_in = 144, height_in = 96, line = 'Thermal Break', thickness_class = '', grilles = null,
          panel_config = '', showDims = false, maxPx = 250, pad = 10 } = opts;
  const { w, h } = aspect(width_in, height_in, maxPx);
  const fw = frameWeight(line, thickness_class);
  const sx = pad, sy = pad;
  const ox = sx + fw, oy = sy + fw, ow = w - 2 * fw, oh = h - 2 * fw;
  const dimPad = showDims ? 26 : 0;
  const cfgLen = String(panel_config || '').toUpperCase().replace(/[^OX]/g, '').length;
  const cols = cfgLen >= 2 ? cfgLen : Math.max(2, Math.min(6, Math.round(width_in / 36)));
  const rows = Math.max(1, Math.min(3, Math.round(height_in / 60)));
  const mull = Math.max(3, fw * 0.7);
  const cw = (ow - mull * (cols - 1)) / cols;
  const rh = (oh - mull * (rows - 1)) / rows;
  let parts = '';
  for (let r = 0; r < rows; r++) {
    for (let cI = 0; cI < cols; cI++) {
      const px = ox + cI * (cw + mull);
      const py = oy + r * (rh + mull);
      parts += glassPanel(px, py, cw, rh);
      parts += grilleXML(grilles, px, py, cw, rh);
    }
  }
  for (let cI = 1; cI < cols; cI++) parts += `<rect x="${ox + cI * (cw + mull) - mull}" y="${oy}" width="${mull}" height="${oh}" fill="${PALETTE.frameFace}"/>`;
  for (let r = 1; r < rows; r++) parts += `<rect x="${ox}" y="${oy + r * (rh + mull) - mull}" width="${ow}" height="${mull}" fill="${PALETTE.frameFace}"/>`;
  return `<svg viewBox="0 0 ${(w + 2 * pad + dimPad).toFixed(0)} ${(h + 2 * pad + dimPad).toFixed(0)}" xmlns="http://www.w3.org/2000/svg">
  ${GLASS_DEFS}
  <rect x="${sx}" y="${sy}" width="${w}" height="${h}" rx="2" fill="${PALETTE.frameFace}" stroke="${PALETTE.frameEdge}" stroke-width="0.75"/>
  ${parts}
  ${showDims ? dimsXML(sx, sy, w, h, width_in, height_in) : ''}
</svg>`;
}
function hingeSide(o) {
  const s = `${o.active_side || ''} ${o.panel_config || ''} ${o.opens || ''}`.toLowerCase();
  return (s.includes('right') || s.includes('rfo')) ? 'r' : 'l';
}
function hasTransom(o) {
  const s = `${o.config || ''} ${o.opens || ''} ${o.product_type || ''}`.toLowerCase();
  return s.includes('transom') || /\bt\b/.test(s) || s.includes('top fixed');
}

function aluElevation(opts = {}) {
  const t = (opts.product_type || '').toLowerCase();
  if (!t) return '';
  const base = {
    width_in:  parseFloat(opts.width_in)  || 36,
    height_in: parseFloat(opts.height_in) || 60,
    line: opts.frame_series || opts.line || '',
    thickness_class: opts.thickness_class || '',
    grilles: parseGrilles(opts),
    showDims: !!opts.showDims,
  };
  if (t.includes('fixed'))    return sashWindow({ ...base, op: 'fixed' });
  if (t.includes('awning'))   return sashWindow({ ...base, op: 'awning' });
  if (t.includes('tilt'))     return sashWindow({ ...base, op: 'tilt-turn' });
  if (t.includes('casement')) return sashWindow({ ...base, op: 'casement-' + hingeSide(opts) });
  if (t.includes('storefront'))
    return singleDoor({ ...base, height_in: parseFloat(opts.height_in) || 96, op: 'hinge-' + hingeSide(opts), style: 'storefront', transom: hasTransom(opts) });
  if (t.includes('window wall') || t.includes('windowwall'))
    return windowWall({ ...base, width_in: parseFloat(opts.width_in) || 144, height_in: parseFloat(opts.height_in) || 96, panel_config: opts.panel_config });  if (t.includes('bifold') || t.includes('folding') || t.includes('bi-fold'))
    return bifold({ ...base, height_in: parseFloat(opts.height_in) || 84, panel_config: opts.panel_config, opens: opts.opens });  if (t.includes('sliding') || t.includes('lift'))
    return slidingTrack({ ...base, height_in: parseFloat(opts.height_in) || 60, panel_config: opts.panel_config });  if (t.includes('french'))
    return doubleDoor({ ...base, height_in: parseFloat(opts.height_in) || 96, transom: hasTransom(opts) });
  if (t.includes('hinged') || (t.includes('door') && !/french|sliding|lift|bifold|window wall/.test(t)))
    return singleDoor({ ...base, height_in: parseFloat(opts.height_in) || 96, op: 'hinge-' + hingeSide(opts), style: 'hinged', transom: hasTransom(opts) });
  return '';
}

export { aluElevation, sashWindow, singleDoor, doubleDoor, slidingTrack, bifold, windowWall, grilleXML, parseGrilles, frameWeight, opGlyph, PALETTE };